import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Download, Eye, EyeOff, ShoppingCart, Package, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import RawMaterialsManager from './RawMaterialsManager';
import FranchiseManager from './FranchiseManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    // Check if already authenticated
    const storedPassword = sessionStorage.getItem('adminPassword');
    if (storedPassword) {
      setPassword(storedPassword);
      setAuthenticated(true);
      fetchOrders(storedPassword);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/admin/verify`, { password });
      setAuthenticated(true);
      sessionStorage.setItem('adminPassword', password); // Store password in session
      toast.success('Authenticated successfully');
      fetchOrders(password);
    } catch (error) {
      console.error('Authentication failed:', error);
      toast.error('Invalid admin password');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async (adminPassword) => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: {
          'X-Admin-Password': adminPassword || password,
        },
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    }
  };

  const exportToCSV = () => {
    if (orders.length === 0) {
      toast.error('No orders to export');
      return;
    }

    const headers = ['Order ID', 'Timestamp', 'Franchise Name', 'Location', 'Contact Number', 'Ordered Items', 'Total Amount'];
    const csvRows = [headers.join(',')];

    orders.forEach(order => {
      const itemsText = order.ordered_items
        .map(item => `${item.item_name} (${item.size_or_quantity}) x${item.quantity}`)
        .join('; ');
      
      const row = [
        order.order_id || 'N/A',
        order.timestamp,
        order.franchise_name,
        order.location,
        order.contact_number,
        `"${itemsText}"`,
        order.total_amount || 0,
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orders_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Orders exported successfully');
  };

  if (!authenticated) {
    return (
      <div style={styles.container}>
        <div style={styles.loginCard}>
          <img src="/assets/logo.png" alt="Prakash Coffee Works" style={styles.logo} />
          <h2 style={styles.loginTitle}>Admin Login</h2>
          <form onSubmit={handleLogin} style={styles.loginForm}>
            <div style={styles.formGroup}>
              <label className="label" htmlFor="password">Admin Password</label>
              <div style={styles.passwordWrapper}>
                <input
                  id="password"
                  data-testid="admin-password-input"
                  type={showPassword ? 'text' : 'password'}
                  className="input-field"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={styles.togglePassword}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              data-testid="login-btn"
              className="btn-primary"
              disabled={loading}
              style={{ width: '100%', opacity: loading ? 0.7 : 1 }}
            >
              {loading ? 'Authenticating...' : 'Login'}
            </button>
            <button
              type="button"
              data-testid="back-to-home-btn"
              className="btn-secondary"
              onClick={() => navigate('/')}
              style={{ width: '100%', marginTop: '12px' }}
            >
              Back to Home
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button
          data-testid="back-btn"
          onClick={() => navigate('/')}
          style={styles.backButton}
        >
          <ArrowLeft size={20} />
        </button>
        <div style={styles.headerCenter}>
          <img src="/assets/logo.png" alt="Logo" style={styles.headerLogo} />
          <h1 style={styles.headerTitle}>Master Admin Dashboard</h1>
        </div>
        <div style={styles.headerRight}></div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          data-testid="orders-tab"
          onClick={() => setActiveTab('orders')}
          style={activeTab === 'orders' ? styles.tabActive : styles.tab}
        >
          <ShoppingCart size={18} />
          Orders
        </button>
        <button
          data-testid="materials-tab"
          onClick={() => setActiveTab('materials')}
          style={activeTab === 'materials' ? styles.tabActive : styles.tab}
        >
          <Package size={18} />
          Raw Materials
        </button>
        <button
          data-testid="franchises-tab"
          onClick={() => setActiveTab('franchises')}
          style={activeTab === 'franchises' ? styles.tabActive : styles.tab}
        >
          <Building2 size={18} />
          Franchises
        </button>
      </div>

      <div className="card" style={styles.dashboardCard}>
        {activeTab === 'orders' && <OrdersView orders={orders} exportToCSV={exportToCSV} password={password} onRefreshOrders={() => fetchOrders(password)} />}
        {activeTab === 'materials' && <RawMaterialsManager password={password} />}
        {activeTab === 'franchises' && <FranchiseManager password={password} />}
      </div>
    </div>
  );
};

const OrdersView = ({ orders, exportToCSV, password, onRefreshOrders }) => {
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [dateFilter, setDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [selectedFranchise, setSelectedFranchise] = useState('all');
  const [viewingOrder, setViewingOrder] = useState(null);

  // Lock body scroll when modal opens
  useEffect(() => {
    if (viewingOrder) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [viewingOrder]);

  // Get unique franchises from orders
  const uniqueFranchises = [...new Set(orders.map(order => order.franchise_name))].sort();

  const filterOrdersByDate = (orders) => {
    if (dateFilter === 'all') return orders;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return orders.filter(order => {
      // Parse timestamp (format: "2025-10-21 07:57:32 UTC")
      const orderDate = new Date(order.timestamp.replace(' UTC', ''));
      
      switch (dateFilter) {
        case 'today':
          return orderDate >= today;
        
        case 'yesterday':
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          return orderDate >= yesterday && orderDate < today;
        
        case 'last7days':
          const last7Days = new Date(today);
          last7Days.setDate(last7Days.getDate() - 7);
          return orderDate >= last7Days;
        
        case 'currentMonth':
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          return orderDate >= monthStart;
        
        case 'lastMonth':
          const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 1);
          return orderDate >= lastMonthStart && orderDate < lastMonthEnd;
        
        case 'currentYear':
          const yearStart = new Date(now.getFullYear(), 0, 1);
          return orderDate >= yearStart;
        
        case 'custom':
          if (!customStartDate || !customEndDate) return true;
          const start = new Date(customStartDate);
          const end = new Date(customEndDate);
          end.setHours(23, 59, 59, 999); // Include entire end date
          return orderDate >= start && orderDate <= end;
        
        default:
          return true;
      }
    });
  };

  const filterOrdersByFranchise = (orders) => {
    if (selectedFranchise === 'all') return orders;
    return orders.filter(order => order.franchise_name === selectedFranchise);
  };

  // Apply both filters
  const dateFilteredOrders = filterOrdersByDate(orders);
  const filteredOrders = filterOrdersByFranchise(dateFilteredOrders);

  const updateOrderStatus = async (rowNumber, field, currentValue) => {
    setUpdatingStatus(`${rowNumber}-${field}`);
    try {
      const newValue = field === 'payment_status' 
        ? (currentValue === 'Paid' ? 'Pending' : 'Paid')
        : (currentValue === 'Delivered' ? 'Pending' : 'Delivered');

      await axios.put(`${API}/admin/order-status`, 
        { 
          row_number: rowNumber,
          [field]: newValue
        },
        { headers: { 'X-Admin-Password': password } }
      );
      
      toast.success('Status updated successfully');
      // Refresh orders without full page reload
      onRefreshOrders();
    } catch (error) {
      toast.error('Failed to update status');
      console.error(error);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
    if (filter === 'custom') {
      setShowCustomRange(true);
    } else {
      setShowCustomRange(false);
    }
  };

  return (
    <>
      {/* Date Filter Section */}
      <div style={styles.filterSection}>
        <h3 style={styles.filterTitle}>Filter Orders:</h3>
        
        {/* Franchise Filter */}
        <div style={styles.franchiseFilterContainer}>
          <label style={styles.franchiseLabel}>By Franchise:</label>
          <select
            value={selectedFranchise}
            onChange={(e) => setSelectedFranchise(e.target.value)}
            style={styles.franchiseSelect}
            data-testid="franchise-filter"
          >
            <option value="all">All Franchises ({orders.length})</option>
            {uniqueFranchises.map((franchise, idx) => {
              const count = orders.filter(o => o.franchise_name === franchise).length;
              return (
                <option key={idx} value={franchise}>
                  {franchise} ({count})
                </option>
              );
            })}
          </select>
        </div>

        {/* Date Filter Buttons */}
        <div style={styles.dateFilterContainer}>
          <label style={styles.franchiseLabel}>By Date:</label>
          <div style={styles.filterButtons}>
            <button
              onClick={() => handleDateFilterChange('all')}
              style={dateFilter === 'all' ? styles.filterButtonActive : styles.filterButton}
              data-testid="filter-all"
            >
              All Orders
            </button>
            <button
              onClick={() => handleDateFilterChange('today')}
              style={dateFilter === 'today' ? styles.filterButtonActive : styles.filterButton}
              data-testid="filter-today"
            >
              Today
            </button>
            <button
              onClick={() => handleDateFilterChange('yesterday')}
              style={dateFilter === 'yesterday' ? styles.filterButtonActive : styles.filterButton}
              data-testid="filter-yesterday"
            >
              Yesterday
            </button>
            <button
              onClick={() => handleDateFilterChange('last7days')}
              style={dateFilter === 'last7days' ? styles.filterButtonActive : styles.filterButton}
              data-testid="filter-last7days"
            >
              Last 7 Days
            </button>
            <button
              onClick={() => handleDateFilterChange('currentMonth')}
              style={dateFilter === 'currentMonth' ? styles.filterButtonActive : styles.filterButton}
              data-testid="filter-current-month"
            >
              Current Month
            </button>
            <button
              onClick={() => handleDateFilterChange('lastMonth')}
              style={dateFilter === 'lastMonth' ? styles.filterButtonActive : styles.filterButton}
              data-testid="filter-last-month"
            >
              Last Month
            </button>
            <button
              onClick={() => handleDateFilterChange('currentYear')}
              style={dateFilter === 'currentYear' ? styles.filterButtonActive : styles.filterButton}
              data-testid="filter-current-year"
            >
              Current Year
            </button>
            <button
              onClick={() => handleDateFilterChange('custom')}
              style={dateFilter === 'custom' ? styles.filterButtonActive : styles.filterButton}
              data-testid="filter-custom"
            >
              Custom Range
            </button>
          </div>

          {showCustomRange && (
            <div style={styles.customRangeContainer}>
              <div style={styles.dateInputGroup}>
                <label style={styles.dateLabel}>From:</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  style={styles.dateInput}
                  data-testid="custom-start-date"
                />
              </div>
              <div style={styles.dateInputGroup}>
                <label style={styles.dateLabel}>To:</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  style={styles.dateInput}
                  data-testid="custom-end-date"
                />
              </div>
            </div>
          )}
        </div>

        <p style={styles.filterResult}>
          Showing <strong>{filteredOrders.length}</strong> of <strong>{orders.length}</strong> orders
        </p>
      </div>

      <div style={styles.ordersHeader}>
        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <h3 style={styles.statNumber}>{filteredOrders.length}</h3>
            <p style={styles.statLabel}>Total Orders</p>
          </div>
          <div style={styles.statCard}>
            <h3 style={styles.statNumber}>
              ₹{filteredOrders.reduce((sum, o) => sum + (o.total_amount || 0), 0).toFixed(2)}
            </h3>
            <p style={styles.statLabel}>Total Revenue</p>
          </div>
        </div>
        <button
          data-testid="export-csv-btn"
          onClick={exportToCSV}
          style={styles.exportButton}
        >
          <Download size={18} style={{ marginRight: '8px' }} />
          Export CSV
        </button>
      </div>

        {filteredOrders.length === 0 ? (
          <div style={styles.emptyState}>
            <p>No orders found for the selected date range</p>
          </div>
        ) : (
          <div style={styles.scrollableTableWrapper}>
            <table style={styles.table} data-testid="orders-table">
              <thead>
                <tr>
                  <th style={styles.th}>Order ID</th>
                  <th style={styles.th}>Date & Time</th>
                  <th style={styles.th}>Franchise</th>
                  <th style={styles.th}>Contact</th>
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Payment</th>
                  <th style={styles.th}>Delivery</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order, index) => (
                  <tr key={index} style={styles.tr} data-testid={`order-row-${index}`}>
                    <td style={styles.td}>
                      <strong style={{color: '#6b4423', fontSize: '0.95em'}}>
                        {order.order_id || 'N/A'}
                      </strong>
                    </td>
                    <td style={styles.td}>{order.timestamp}</td>
                    <td style={styles.td}>
                      <strong>{order.franchise_name}</strong>
                      <br />
                      <small style={{color: '#8b5e34'}}>{order.location}</small>
                    </td>
                    <td style={styles.td}>{order.contact_number}</td>
                    <td style={styles.td}>
                      <strong>Rs. {order.total_amount || 0}</strong>
                    </td>
                    <td style={styles.td}>
                      <label style={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={order.payment_status === 'Paid'}
                          onChange={() => updateOrderStatus(order.row_number, 'payment_status', order.payment_status)}
                          disabled={updatingStatus === `${order.row_number}-payment_status`}
                          style={styles.checkbox}
                          data-testid={`payment-checkbox-${index}`}
                        />
                        <span style={order.payment_status === 'Paid' ? styles.statusPaid : styles.statusPending}>
                          {order.payment_status === 'Paid' ? 'Paid' : 'Pending'}
                        </span>
                      </label>
                    </td>
                    <td style={styles.td}>
                      <label style={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={order.delivery_status === 'Delivered'}
                          onChange={() => updateOrderStatus(order.row_number, 'delivery_status', order.delivery_status)}
                          disabled={updatingStatus === `${order.row_number}-delivery_status`}
                          style={styles.checkbox}
                          data-testid={`delivery-checkbox-${index}`}
                        />
                        <span style={order.delivery_status === 'Delivered' ? styles.statusDelivered : styles.statusPending}>
                          {order.delivery_status === 'Delivered' ? 'Delivered' : 'Pending'}
                        </span>
                      </label>
                    </td>
                    <td style={styles.td}>
                      <button
                        onClick={() => setViewingOrder(order)}
                        style={styles.viewButton}
                        data-testid={`view-order-${index}`}
                      >
                        <Eye size={16} style={{ marginRight: '4px' }} />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {/* PDF Viewer Modal */}
      {viewingOrder && (
        <div style={styles.pdfModal} onClick={() => setViewingOrder(null)}>
          <div style={styles.pdfModalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.pdfModalHeader}>
              <h3 style={styles.pdfModalTitle}>Order Details - {viewingOrder.order_id || 'N/A'}</h3>
              <button onClick={() => setViewingOrder(null)} style={styles.pdfCloseBtn} title="Close">×</button>
            </div>
            <div style={styles.pdfModalBody}>
              {viewingOrder.pdf_url ? (
                <>
                  <iframe
                    src={viewingOrder.pdf_url}
                    style={styles.pdfIframe}
                    title="Order PDF"
                  />
                  <div style={styles.pdfActions}>
                    <button
                      onClick={() => window.open(viewingOrder.pdf_url, '_blank')}
                      style={styles.downloadButton}
                    >
                      <Download size={18} style={{ marginRight: '8px' }} />
                      Download PDF
                    </button>
                  </div>
                </>
              ) : (
                <div style={styles.noPdfMessage}>
                  <p>PDF not available for this order.</p>
                  <div style={styles.orderDetailsFallback}>
                    <h4>Order Items:</h4>
                    <ul style={styles.itemsList}>
                      {viewingOrder.ordered_items.map((item, idx) => (
                        <li key={idx} style={styles.itemText}>
                          {item.item_name} ({item.size_or_quantity}) x {item.quantity} - Rs. {item.total_price}
                        </li>
                      ))}
                    </ul>
                    <p style={{marginTop: '16px', fontSize: '18px', fontWeight: 'bold'}}>
                      Total: Rs. {viewingOrder.total_amount}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </>
    );
};

const styles = {
  container: {
    minHeight: '100vh',
    padding: '20px',
  },
  loginCard: {
    maxWidth: '400px',
    margin: '100px auto',
    background: 'white',
    borderRadius: '16px',
    padding: '48px 32px',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    textAlign: 'center',
  },
  logo: {
    width: '120px',
    height: '120px',
    objectFit: 'contain',
    margin: '0 auto 16px',
  },
  loginTitle: {
    fontSize: '28px',
    color: '#6b4423',
    margin: '16px 0 24px',
    fontFamily: "'League Spartan', sans-serif",
    fontWeight: '700',
  },
  loginForm: {
    marginTop: '24px',
  },
  formGroup: {
    marginBottom: '24px',
    textAlign: 'left',
  },
  passwordWrapper: {
    position: 'relative',
  },
  togglePassword: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#8b5e34',
  },
  header: {
    maxWidth: '1200px',
    margin: '0 auto 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  backButton: {
    background: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  headerCenter: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flex: 1,
  },
  headerLogo: {
    width: '48px',
    height: '48px',
    objectFit: 'contain',
  },
  headerTitle: {
    fontSize: '32px',
    color: '#6b4423',
    fontFamily: "'League Spartan', sans-serif",
    fontWeight: '700',
    margin: 0,
  },
  headerRight: {
    width: '48px',
  },
  tabs: {
    maxWidth: '1200px',
    margin: '0 auto 24px',
    display: 'flex',
    gap: '12px',
    background: 'white',
    padding: '8px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'transparent',
    border: 'none',
    borderRadius: '8px',
    padding: 'clamp(10px, 2vw, 12px) clamp(16px, 3vw, 24px)',
    cursor: 'pointer',
    fontSize: 'clamp(14px, 2vw, 16px)',
    fontWeight: '500',
    color: '#6b5d54',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
    minWidth: 'fit-content',
  },
  tabActive: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'linear-gradient(135deg, #6b4423 0%, #8b5e34 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    boxShadow: '0 2px 8px rgba(107, 68, 35, 0.3)',
  },
  ordersHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px',
  },
  exportButton: {
    display: 'flex',
    alignItems: 'center',
    background: '#6b4423',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
  },
  dashboardCard: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    flex: 1,
  },
  statCard: {
    background: 'linear-gradient(135deg, #6b4423 0%, #8b5e34 100%)',
    borderRadius: '12px',
    padding: '24px',
    textAlign: 'center',
    color: 'white',
  },
  statNumber: {
    fontSize: '36px',
    fontWeight: '700',
    margin: '0 0 8px',
  },
  statLabel: {
    fontSize: '16px',
    opacity: 0.9,
  },
  emptyState: {
    padding: '64px',
    textAlign: 'center',
    color: '#8b5e34',
    background: '#f9f6f1',
    borderRadius: '8px',
  },
  tableWrapper: {
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    marginBottom: '20px',
  },
  scrollableTableWrapper: {
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: '500px',
    WebkitOverflowScrolling: 'touch',
    marginBottom: '20px',
    border: '1px solid #e8dcc8',
    borderRadius: '8px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '800px', // Force horizontal scroll on mobile
  },
  th: {
    textAlign: 'left',
    padding: '16px',
    background: '#f9f6f1',
    color: '#6b4423',
    fontWeight: '600',
    borderBottom: '2px solid #e8dcc8',
  },
  tr: {
    borderBottom: '1px solid #e8dcc8',
  },
  td: {
    padding: '16px',
    color: '#4a3728',
  },
  itemsList: {
    margin: 0,
    paddingLeft: '20px',
  },
  itemText: {
    fontSize: '14px',
    color: '#6b5d54',
    marginBottom: '4px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: '#16a34a',
  },
  statusPaid: {
    color: '#16a34a',
    fontWeight: '600',
    fontSize: '14px',
  },
  statusDelivered: {
    color: '#16a34a',
    fontWeight: '600',
    fontSize: '14px',
  },
  statusPending: {
    color: '#dc2626',
    fontWeight: '600',
    fontSize: '14px',
  },
  filterSection: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  filterTitle: {
    fontSize: '16px',
    color: '#6b4423',
    marginBottom: '16px',
    fontWeight: '600',
  },
  filterButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '16px',
  },
  filterButton: {
    background: 'white',
    border: '2px solid #e8dcc8',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#6b4423',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  filterButtonActive: {
    background: 'linear-gradient(135deg, #6b4423 0%, #8b5e34 100%)',
    border: '2px solid #6b4423',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '600',
    color: 'white',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(107, 68, 35, 0.3)',
  },
  customRangeContainer: {
    display: 'flex',
    gap: '16px',
    marginTop: '16px',
    padding: '16px',
    background: '#f9f6f1',
    borderRadius: '8px',
  },
  dateInputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  dateLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#6b4423',
  },
  dateInput: {
    padding: '8px 12px',
    border: '2px solid #e8dcc8',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'Inter, sans-serif',
  },
  filterResult: {
    fontSize: '14px',
    color: '#6b5d54',
    marginTop: '12px',
  },
  franchiseFilterContainer: {
    marginBottom: '20px',
    padding: '16px',
    background: '#f9f6f1',
    borderRadius: '8px',
  },
  franchiseLabel: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#6b4423',
    marginBottom: '8px',
  },
  franchiseSelect: {
    width: '100%',
    padding: '10px 12px',
    border: '2px solid #e8dcc8',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'Inter, sans-serif',
    background: 'white',
    cursor: 'pointer',
    color: '#6b4423',
  },
  dateFilterContainer: {
    marginBottom: '16px',
  },
  viewButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background 0.2s',
  },
  pdfModal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '20px',
  },
  pdfModalContent: {
    background: 'white',
    borderRadius: '16px',
    width: '90%',
    maxWidth: '900px',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
    overflow: 'hidden',
  },
  pdfModalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '2px solid #e8dcc8',
  },
  pdfModalTitle: {
    fontSize: '20px',
    color: '#6b4423',
    margin: 0,
    fontWeight: '700',
  },
  pdfCloseBtn: {
    background: 'none',
    border: 'none',
    fontSize: '32px',
    color: '#6b4423',
    cursor: 'pointer',
    padding: '0',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: '1',
  },
  pdfModalBody: {
    flex: 1,
    overflow: 'auto',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
  },
  pdfIframe: {
    width: '100%',
    height: '600px',
    border: '2px solid #e8dcc8',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  pdfActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
  },
  downloadButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#16a34a',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    transition: 'background 0.2s',
  },
  noPdfMessage: {
    textAlign: 'center',
    padding: '40px',
    color: '#6b5d54',
  },
  orderDetailsFallback: {
    textAlign: 'left',
    marginTop: '24px',
    padding: '24px',
    background: '#f9f6f1',
    borderRadius: '8px',
  },
};

export default AdminDashboard;